import type { Dispatch } from 'react';
import { ItemStatusEnum, type QueueItemPoll } from '@pink/shared';

import { decryptResponse } from '../../crypto-module/decrypt-response.js';
import { clearPatientKeysFromIDB } from '../../crypto-module/clear-patient-keys-from-idb.js';
import { BusinessErrorReason } from '../state-machine-feature/business-error-reason.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';
import type { CancelAction } from '../state-machine-feature/cancel-action.type.js';

/**
 * Ergebnis-Diskriminante für einen Tick des Polling-Loops.
 *
 * - `continue`: Status war `pending` — nächster Tick einplanen.
 * - `terminal`: Queue-Item hat `success` oder `error` erreicht. Loop
 *   beendet, kein weiterer Tick.
 */
type PollTickOutcome = 'continue' | 'terminal';

interface HandleStatusArgs {
  readonly response: QueueItemPoll;
  readonly privateKey: CryptoKey;
  readonly dispatch: Dispatch<CancelAction>;
}

/**
 * Verarbeitet eine `QueueItemPoll`-Antwort. Bei `pending` signalisiert die
 * Funktion mit `'continue'`, dass der nächste Tick eingeplant werden soll.
 *
 * Bei `success`/`error` wird der optionale Praxis-`payload` (Spec-Erweiterung
 * Pilot-Stack) mit dem Patient-Privatkey entschlüsselt — der Klartext-Status
 * (`SUCCESS`/`REJECTED`) entscheidet, in welchen End-Screen die State-Machine
 * fällt.
 *
 * Fehlt der `payload` (Praxis hat nur den Status gesetzt) oder schlägt der
 * Decrypt fehl, fällt das Verhalten auf den HTTP-Status zurück:
 *   - `success` ohne lesbare Antwort → `GOTO_SUCCESS` (best-effort, der Auftrag
 *     gilt aus Sicht der Queue als erledigt).
 *   - `error` ohne lesbare Antwort → `GOTO_BUSINESS_ERROR` `PracticeRejected`.
 *
 * Decrypt-Fehler werden via `console.warn('decrypt_failed')` markiert, damit
 * sie in den DevTools als Tampering-/Key-Drift-Indikator sichtbar sind.
 */
export async function handleStatusResponse(args: HandleStatusArgs): Promise<PollTickOutcome> {
  if (args.response.status === ItemStatusEnum.Pending) {
    return 'continue';
  }
  await dispatchTerminal(args);
  return 'terminal';
}

async function dispatchTerminal(args: HandleStatusArgs): Promise<void> {
  const outcome = await resolveTerminal(args);
  if (outcome === 'handled') {
    await safeClearIdb();
    return;
  }
  // Fallback auf den HTTP-Status, wenn der Praxis-Payload entweder fehlt
  // oder nicht entschlüsselbar war (und KEIN Tech-Error gesetzt wurde).
  if (args.response.status === ItemStatusEnum.Success) {
    args.dispatch({ type: 'GOTO_SUCCESS' });
  } else {
    args.dispatch({
      type: 'GOTO_BUSINESS_ERROR',
      reason: BusinessErrorReason.PracticeRejected,
    });
  }
  await safeClearIdb();
}

type ResolveOutcome = 'handled' | 'fallback';

async function resolveTerminal(args: HandleStatusArgs): Promise<ResolveOutcome> {
  if (typeof args.response.payload !== 'string' || args.response.payload.length === 0) {
    return 'fallback';
  }
  try {
    const decrypted = await decryptResponse(args.response.payload, args.privateKey);
    if (decrypted.status === 'SUCCESS') {
      args.dispatch({ type: 'GOTO_SUCCESS' });
    } else {
      args.dispatch({
        type: 'GOTO_BUSINESS_ERROR',
        reason: BusinessErrorReason.PracticeRejected,
      });
    }
    return 'handled';
  } catch {
    // PII-freier Marker (Tampering-/Key-Drift-Indikator).
    console.warn('decrypt_failed');
    args.dispatch({
      type: 'GOTO_TECHNICAL_ERROR',
      variant: TechnicalErrorVariant.Generic,
    });
    return 'handled';
  }
}

async function safeClearIdb(): Promise<void> {
  try {
    await clearPatientKeysFromIDB();
  } catch {
    // IDB-Cleanup-Fehler dürfen den End-State nicht verschlucken.
  }
}
